import { useState } from "react";
import { mockBankAccounts } from "@/mocks/dealerData";

interface BankAccount {
  id: string;
  bankName: string;
  branchCode: string;
  branchName: string;
  ibanNumber: string;
  accountHolder: string;
  accountNumber: string;
  currency: string;
  isActive: boolean;
}

export default function AdminBankAccountsPage() {
  const [accounts, setAccounts] = useState<BankAccount[]>(mockBankAccounts);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<BankAccount | null>(null);
  const [form, setForm] = useState({
    bankName: "",
    branchCode: "",
    branchName: "",
    ibanNumber: "",
    accountHolder: "",
    accountNumber: "",
    currency: "TL",
    isActive: true,
  });
  const [filterActive, setFilterActive] = useState<boolean | null>(null);

  const filteredAccounts = filterActive === null ? accounts : accounts.filter((a) => a.isActive === filterActive);

  const openAdd = () => {
    setEditing(null);
    setForm({ bankName: "", branchCode: "", branchName: "", ibanNumber: "", accountHolder: "", accountNumber: "", currency: "TL", isActive: true });
    setShowModal(true);
  };

  const openEdit = (account: BankAccount) => {
    setEditing(account);
    setForm({
      bankName: account.bankName,
      branchCode: account.branchCode,
      branchName: account.branchName,
      ibanNumber: account.ibanNumber,
      accountHolder: account.accountHolder,
      accountNumber: account.accountNumber,
      currency: account.currency,
      isActive: account.isActive,
    });
    setShowModal(true);
  };

  const save = () => {
    if (!form.bankName.trim() || !form.ibanNumber.trim() || !form.accountHolder.trim()) return;
    if (editing) {
      setAccounts((prev) => prev.map((a) => (a.id === editing.id ? { ...a, ...form } : a)));
    } else {
      setAccounts((prev) => [...prev, { id: "ba" + Date.now(), ...form }]);
    }
    setShowModal(false);
  };

  const toggleActive = (id: string) => {
    setAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, isActive: !a.isActive } : a)));
  };

  const deleteAccount = () => {
    if (!deleteConfirm) return;
    setAccounts((prev) => prev.filter((a) => a.id !== deleteConfirm.id));
    setDeleteConfirm(null);
  };

  const ibanFormat = (value: string) => {
    // Format: TRxx xxxx xxxx xxxx xxxx xxxx xx
    const cleaned = value.replace(/\s/g, "").toUpperCase();
    if (cleaned.length <= 2) return cleaned;
    let formatted = cleaned.slice(0, 2);
    for (let i = 2; i < cleaned.length; i += 4) {
      formatted += " " + cleaned.slice(i, i + 4);
    }
    return formatted;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">
            <a id="bank-accounts-title">Banka Tanımlamaları</a>
          </h4>
          <p className="text-sm text-foreground-500 mt-0.5">Sisteme tanımlı banka hesaplarını yönetin. Aktif hesaplar bayi satış ekranında IBAN seçimi için listelenir.</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i> Banka Hesabı Ekle
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setFilterActive(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
            filterActive === null ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-500 hover:bg-background-200/70 border border-background-200/70"
          }`}
        >
          Tümü ({accounts.length})
        </button>
        <button
          onClick={() => setFilterActive(true)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
            filterActive === true ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-500 hover:bg-background-200/70 border border-background-200/70"
          }`}
        >
          Aktif ({accounts.filter((a) => a.isActive).length})
        </button>
        <button
          onClick={() => setFilterActive(false)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
            filterActive === false ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-500 hover:bg-background-200/70 border border-background-200/70"
          }`}
        >
          Pasif ({accounts.filter((a) => !a.isActive).length})
        </button>
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {/* Desktop Table */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Banka</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Şube</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">IBAN</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Hesap Sahibi</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Para Birimi</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Durum</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-foreground-400">
                    <i className="ri-bank-line text-3xl block mb-2"></i>
                    <p className="text-sm">Henüz banka hesabı tanımlanmamış.</p>
                  </td>
                </tr>
              ) : (
                filteredAccounts.map((account) => (
                  <tr key={account.id} className={`border-t border-background-200/70 hover:bg-background-100/50 transition-colors ${!account.isActive ? "opacity-60" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${account.isActive ? "bg-primary-100" : "bg-background-200/70"}`}>
                          <i className={`ri-bank-line text-sm ${account.isActive ? "text-primary-600" : "text-foreground-400"}`}></i>
                        </div>
                        <span className="font-medium text-foreground-900">{account.bankName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-foreground-600">
                      <span className="text-xs text-foreground-400">{account.branchCode} - </span>
                      {account.branchName}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground-700">{account.ibanNumber}</td>
                    <td className="px-4 py-3 text-foreground-700">{account.accountHolder}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${account.currency === "TL" ? "bg-secondary-100 text-secondary-700" : "bg-accent-100 text-accent-700"}`}>
                        {account.currency}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(account.id)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          account.isActive ? "bg-primary-100 text-primary-700 hover:bg-primary-200/70" : "bg-background-200/70 text-foreground-400 hover:bg-background-300/60"
                        }`}
                      >
                        <i className={`text-[10px] ${account.isActive ? "ri-checkbox-circle-fill" : "ri-indeterminate-circle-line"}`}></i>
                        {account.isActive ? "Aktif" : "Pasif"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(account)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button onClick={() => setDeleteConfirm(account)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-background-200/70">
          {filteredAccounts.length === 0 ? (
            <div className="px-4 py-10 text-center text-foreground-400">
              <i className="ri-bank-line text-3xl block mb-2"></i>
              <p className="text-sm">Henüz banka hesabı tanımlanmamış.</p>
            </div>
          ) : (
            filteredAccounts.map((account) => (
              <div key={account.id} className={`p-4 ${!account.isActive ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${account.isActive ? "bg-primary-100" : "bg-background-200/70"}`}>
                      <i className={`ri-bank-line text-sm ${account.isActive ? "text-primary-600" : "text-foreground-400"}`}></i>
                    </div>
                    <div>
                      <p className="font-medium text-foreground-900 text-sm">{account.bankName}</p>
                      <p className="text-xs text-foreground-400">{account.branchCode} - {account.branchName}</p>
                    </div>
                  </div>
                  <button onClick={() => toggleActive(account.id)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${account.isActive ? "bg-primary-100 text-primary-700 hover:bg-primary-200/70" : "bg-background-200/70 text-foreground-400 hover:bg-background-300/60"}`}>
                    <i className={`text-[10px] ${account.isActive ? "ri-checkbox-circle-fill" : "ri-indeterminate-circle-line"}`}></i>
                    {account.isActive ? "Aktif" : "Pasif"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mb-2">
                  <div><span className="text-foreground-400">IBAN:</span> <span className="text-foreground-700 font-mono">{account.ibanNumber}</span></div>
                  <div><span className="text-foreground-400">Hesap Sahibi:</span> <span className="text-foreground-700">{account.accountHolder}</span></div>
                  <div><span className="text-foreground-400">Hesap No:</span> <span className="text-foreground-700">{account.accountNumber || "-"}</span></div>
                  <div><span className="text-foreground-400">Para Birimi:</span> <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${account.currency === "TL" ? "bg-secondary-100 text-secondary-700" : "bg-accent-100 text-accent-700"}`}>{account.currency}</span></div>
                </div>
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => openEdit(account)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer active:scale-[0.98]"><i className="ri-edit-line text-sm"></i></button>
                  <button onClick={() => setDeleteConfirm(account)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer active:scale-[0.98]"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-base font-semibold text-foreground-950">
                <a id="bank-form">{editing ? "Banka Hesabı Düzenle" : "Yeni Banka Hesabı"}</a>
              </h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground-500 mb-1">Banka Adı *</label>
                <input
                  type="text"
                  value={form.bankName}
                  onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                  placeholder="Örn: Ziraat Bankası"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Şube Kodu</label>
                <input
                  type="text"
                  value={form.branchCode}
                  onChange={(e) => setForm({ ...form, branchCode: e.target.value })}
                  placeholder="Örn: 0001"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Şube Adı</label>
                <input
                  type="text"
                  value={form.branchName}
                  onChange={(e) => setForm({ ...form, branchName: e.target.value })}
                  placeholder="Örn: Merkez Şube"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground-500 mb-1">IBAN *</label>
                <input
                  type="text"
                  value={form.ibanNumber}
                  onChange={(e) => setForm({ ...form, ibanNumber: ibanFormat(e.target.value) })}
                  placeholder="TRxx xxxx xxxx xxxx xxxx xxxx xx"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm font-mono text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground-500 mb-1">Hesap Sahibi *</label>
                <input
                  type="text"
                  value={form.accountHolder}
                  onChange={(e) => setForm({ ...form, accountHolder: e.target.value })}
                  placeholder="Örn: ART POS Ticaret A.Ş."
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Hesap Numarası</label>
                <input
                  type="text"
                  value={form.accountNumber}
                  onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
                  placeholder="Hesap numarası"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Para Birimi</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                >
                  <option value="TL">TL</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
                  />
                  <span className="text-sm text-foreground-700">Hesap aktif (satış ekranında görünür)</span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                İptal
              </button>
              <button
                onClick={save}
                disabled={!form.bankName.trim() || !form.ibanNumber.trim() || !form.accountHolder.trim()}
                className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {editing ? "Güncelle" : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-error-warning-line text-accent-600 text-xl"></i>
              </div>
              <h4 className="text-base font-semibold text-foreground-950 mb-1"><a id="delete-confirm">Hesabı Sil</a></h4>
              <p className="text-sm text-foreground-500">
                <strong>{deleteConfirm.bankName}</strong> - {deleteConfirm.ibanNumber} hesabını silmek istediğinize emin misiniz?
              </p>
              <p className="text-xs text-foreground-400 mt-1">Bu işlem geri alınamaz.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                İptal
              </button>
              <button onClick={deleteAccount} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}